import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'

export interface DocumentFile {
  url: string
  filename: string
  kind: 'unsigned' | 'signed-original'
  expires_in: number
}

export async function requestDocumentFile(
  token: string,
  documentId: string,
  download = false
): Promise<DocumentFile> {
  return fetchJsonWithAuth<DocumentFile>({
    token,
    url: `${AGENT_URL}/signing/documents/${encodeURIComponent(documentId)}/file?download=${download}`,
    errorMessage: 'Document is not available to download.',
  })
}

export function DocumentDownload({ documentId }: { documentId: string }) {
  const { getToken } = useAuth()
  const [pending, setPending] = useState(false)
  async function download() {
    setPending(true)
    try {
      const file = await requestDocumentFile(
        await requireAuthToken(getToken),
        documentId,
        true
      )
      const link = document.createElement('a')
      link.href = file.url
      link.download = file.filename
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {
      toast.error('Could not download document. Please try again.')
    } finally {
      setPending(false)
    }
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      className="cursor-pointer"
      disabled={pending}
      onClick={download}>
      {pending ? <Loader2 className="animate-spin" /> : <Download />}
      Download
    </Button>
  )
}
